/**
 * ACIL Intelligence - Setup 2FA Page
 * Three-step process: Instructions, QR Code, Verify Code
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../utils/api';
import { Shield, Smartphone, QrCode, CheckCircle2, Copy, AlertCircle, Loader2 } from 'lucide-react';
import '../App.css';

export default function Setup2FA() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/api/auth/setup-2fa');
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/api/auth/verify-2fa-setup', { code });
      if (data.backup_codes) {
        setBackupCodes(data.backup_codes);
        setStep(3);
      }
      if (data.user) {
        setUser(data.user);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const goToDashboard = () => navigate('/dashboard');

  return (
    <div className="auth-page" data-testid="setup-2fa-page">
      <div className="auth-card" style={{ maxWidth: 480 }}>
        <div className="auth-logo">
          <div className="auth-logo-icon"><Shield size={22} /></div>
          <span className="auth-logo-text">ACIL Intelligence</span>
        </div>

        {/* Step Indicator */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
          {[1, 2, 3].map((s) => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: s <= step ? 'var(--orange)' : 'var(--border)',
              transition: 'background 0.3s ease',
            }} />
          ))}
        </div>

        {error && (
          <div className="auth-error-banner" data-testid="2fa-error">
            <AlertCircle size={16} /><span>{error}</span>
          </div>
        )}

        {/* Step 1: Instructions */}
        {step === 1 && (
          <div data-testid="2fa-step-1">
            <h1 className="auth-title" style={{ fontSize: 22 }}>Configurar 2FA</h1>
            <p className="auth-subtitle">
            Tu rol requiere autenticación de dos factores. Necesitarás una app de autenticación.
            </p>
            <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 24, marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <Smartphone size={20} color="var(--orange)" />
                <span style={{ fontSize: 14, fontWeight: 600 }}>Instala una app de autenticación</span>
              </div>
              <ul style={{ fontSize: 13, color: 'var(--muted)', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 32 }}>
                <li>Google Authenticator (iOS / Android)</li>
                <li>Microsoft Authenticator (iOS / Android)</li>
                <li>Authy (iOS / Android / Desktop)</li>
              </ul>
            </div>
            <button className="btn-primary" onClick={startSetup} disabled={loading} style={{ width: '100%', height: 48 }} data-testid="start-2fa-setup-btn">
              {loading ? <><div className="acil-spinner" /> Generando...</> : 'Continuar'}
            </button>
          </div>
        )}

        {/* Step 2: QR Code + Verify */}
        {step === 2 && (
          <div data-testid="2fa-step-2">
            <h1 className="auth-title" style={{ fontSize: 22 }}>Escanea el código QR</h1>
            <p className="auth-subtitle">
              Abre tu app de autenticación y escanea este código QR.
            </p>
            {qrCode && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <div style={{ background: 'white', padding: 16, borderRadius: 16, border: '1px solid var(--border)' }}>
                  <img src={qrCode} alt="2FA QR Code" style={{ width: 200, height: 200 }} data-testid="2fa-qr-code" />
                </div>
              </div>
            )}
            <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 14, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Clave manual</div>
                <code style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--dark)' }} data-testid="2fa-secret">{secret}</code>
              </div>
              <button onClick={copySecret} className="btn-ghost" data-testid="copy-secret-btn">
                <Copy size={16} /> {copied ? 'Copiado' : 'Copiar'}
              </button>
            </div>
            <form onSubmit={verifyCode}>
              <label className="acil-label">Código de verificación</label>
              <input
                type="text"
                className="acil-input"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                style={{ textAlign: 'center', fontSize: 24, fontWeight: 700, letterSpacing: '0.2em' }}
                autoFocus
                data-testid="2fa-code-input"
              />
              <button type="submit" className="btn-primary" disabled={loading || code.length !== 6} style={{ width: '100%', height: 48, marginTop: 16 }} data-testid="verify-2fa-setup-btn">
                {loading ? <><div className="acil-spinner" /> Verificando...</> : 'Verificar y activar'}
              </button>
            </form>
          </div>
        )}

        {/* Step 3: Backup Codes */}
        {step === 3 && (
          <div data-testid="2fa-step-3">
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <CheckCircle2 size={28} color="var(--success)" />
              </div>
              <h1 className="auth-title" style={{ fontSize: 22 }}>Autenticación de dos pasos activada</h1>
              <p className="auth-subtitle" style={{ marginBottom: 8 }}>
                Guarda estos códigos de respaldo en un lugar seguro. Son de un solo uso y no se mostrarán de nuevo.
              </p>
            </div>
            <div className="backup-codes-grid" data-testid="backup-codes-grid">
              {backupCodes.map((c, i) => (
                <div key={i} className="backup-code" data-testid={`backup-code-${i}`}>{c}</div>
              ))}
            </div>
            <button onClick={copyBackupCodes} className="btn-secondary" style={{ width: '100%', marginBottom: 12 }} data-testid="copy-backup-codes-btn">
              <Copy size={16} /> {copied ? 'Copiados' : 'Copiar códigos'}
            </button>
            <button onClick={goToDashboard} className="btn-primary" style={{ width: '100%', height: 48 }} data-testid="go-to-dashboard-btn">
              Ir al inicio
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
